/**
 * Analytics Debugger Utility
 * Professional debugging tool for support team
 * Works in production with secure activation methods
 */

class AnalyticsDebugger {
  constructor() {
    this.enabled = false;
    this.events = [];
    this.maxEvents = 100;
    this.panel = null;
    this.startTime = Date.now();
    this.backendConfig = {};
    this.activationMethods = {
      backend: false,
      url: false,
      localStorage: false,
      secretKey: false,
      timeWindow: false
    };

    // Will be initialized later with backend config
  }

  /**
   * Initialize with backend configuration
   */
  initialize(backendConfig, networkData) {
    this.backendConfig = backendConfig || {};
    this.networkData = networkData || {};

    console.log('[Analytics Debugger] Initializing with backend config:', this.backendConfig);
    console.log('[Analytics Debugger] Network data available:', !!networkData);

    // Check if debugging is force-enabled in backend
    if (this.backendConfig.enabled === true) {
      this.activationMethods.backend = true;
      console.log('[Analytics Debugger] Force-enabled by backend configuration');
      this.enable('Backend Force Enable');
      return;
    }

    // Check if debugging is completely disabled
    if (this.backendConfig.allowed === false) {
      console.log('[Analytics Debugger] Completely disabled by backend configuration');
      this.showDisabledMessage();
      return;
    }

    // Only check other activation methods if backend allows it
    console.log('[Analytics Debugger] Manual activation allowed, checking activation methods');
    this.showConfigStatus();
    this.checkActivation();
  }

  /**
   * Check all activation methods
   */
  checkActivation() {
    if (typeof window !== 'undefined') {
      // Only allow manual activation if backend permits it
      const isAllowed = this.backendConfig.analytics_debug_allowed !== false;

      if (!isAllowed) {
        console.log('Analytics debugging is disabled by backend configuration');
        return;
      }

      // Method 1: URL Parameter (?analytics_debug=true)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('analytics_debug') === 'true') {
        this.activationMethods.url = true;
        this.enable('URL Parameter');
      }

      // Method 2: LocalStorage Flag
      if (localStorage.getItem('analytics_debug') === 'enabled') {
        this.activationMethods.localStorage = true;
        this.enable('LocalStorage');
      }

      // Method 3: Secret Key Combination (Ctrl+Shift+D twice within 1 second)
      // Only if backend allows keyboard activation
      if (this.backendConfig.analytics_debug_keyboard !== false) {
        this.setupKeyboardActivation();
      }

      // Method 4: Time-based activation (only active for 30 minutes after enabling)
      const debugTimestamp = localStorage.getItem('analytics_debug_timestamp');
      if (debugTimestamp) {
        const elapsed = Date.now() - parseInt(debugTimestamp);
        const timeLimit = this.backendConfig.analytics_debug_time_limit || (30 * 60 * 1000);
        if (elapsed < timeLimit) {
          this.activationMethods.timeWindow = true;
          this.enable('Time Window');
        } else {
          localStorage.removeItem('analytics_debug_timestamp');
        }
      }

      // Method 5: Support Team Hash (for remote activation)
      if (urlParams.get('support_key')) {
        this.validateSupportKey(urlParams.get('support_key'));
      }
    }
  }

  /**
   * Setup keyboard shortcut activation
   */
  setupKeyboardActivation() {
    let lastKeyPress = 0;
    let keyPressCount = 0;

    document.addEventListener('keydown', (e) => {
      // Ctrl+Shift+D
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();

        const now = Date.now();
        if (now - lastKeyPress < 1000) {
          keyPressCount++;
          if (keyPressCount >= 2) {
            this.activationMethods.secretKey = true;
            this.toggle('Keyboard Shortcut');
            keyPressCount = 0;
          }
        } else {
          keyPressCount = 1;
        }
        lastKeyPress = now;
      }
    });
  }

  /**
   * Validate support team activation key
   */
  validateSupportKey(key) {
    // Simple validation - in production, this could check against a backend API
    const today = new Date().toISOString().split('T')[0];
    const expectedKey = btoa('analytics_debug_' + today).substring(0, 10);

    if (key === expectedKey) {
      this.activationMethods.secretKey = true;
      this.enable('Support Key');

      // Set time window
      localStorage.setItem('analytics_debug_timestamp', Date.now().toString());
    }
  }

  /**
   * Enable debugging
   */
  enable(method = 'Manual') {
    if (this.enabled) return;

    this.enabled = true;
    localStorage.setItem('analytics_debug', 'enabled');
    localStorage.setItem('analytics_debug_timestamp', Date.now().toString());

    console.log(`%c📊 Analytics Debugger Enabled`, 'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px;', `via ${method}`);

    this.createDebugPanel();
    this.interceptAnalytics();

    // Show activation notification
    this.showNotification(`Analytics Debugger Enabled via ${method}`, 'success');
  }

  /**
   * Disable debugging
   */
  disable() {
    this.enabled = false;
    localStorage.removeItem('analytics_debug');
    localStorage.removeItem('analytics_debug_timestamp');

    console.log(`%c📊 Analytics Debugger Disabled`, 'background: #f44336; color: white; padding: 5px 10px; border-radius: 3px;');

    if (this.panel) {
      this.panel.remove();
      this.panel = null;
    }

    this.showNotification('Analytics Debugger Disabled', 'info');
  }

  /**
   * Toggle debugging
   */
  toggle(method = 'Manual') {
    if (this.enabled) {
      this.disable();
    } else {
      this.enable(method);
    }
  }

  /**
   * Show message when debugger is disabled by backend
   */
  showDisabledMessage() {
    console.log('%c📊 Analytics Debugger Disabled', 'background: #f44336; color: white; padding: 5px 10px; border-radius: 3px;', 'Debugging is disabled by backend configuration');

    this.showNotification('Analytics Debugger is disabled by configuration', 'info');
  }

  /**
   * Show configuration status message
   */
  showConfigStatus() {
    const status = {
      enabled: this.backendConfig.enabled || false,
      allowed: this.backendConfig.allowed !== false,
      keyboardActivation: this.backendConfig.keyboard_activation !== false,
      timeLimit: this.backendConfig.time_limit_minutes || 30,
      supportOnly: this.backendConfig.support_only || false
    };

    console.log('%c📊 Analytics Debugger Config', 'background: #2196F3; color: white; padding: 5px 10px; border-radius: 3px;', status);

    if (status.enabled) {
      console.log('🟢 Force-enabled by backend');
    } else if (status.allowed) {
      console.log('🟡 Manual activation allowed');
      console.log('🔑 Activation methods:');
      console.log('  • URL parameter: ?analytics_debug=true');
      if (status.keyboardActivation) {
        console.log('  • Keyboard: Ctrl+Shift+D (twice)');
      }
      console.log('  • LocalStorage: localStorage.setItem("analytics_debug", "enabled")');
      console.log('  • Support key: ?support_key=DAILY_KEY');
    } else {
      console.log('🔴 Completely disabled by backend');
    }
  }

  /**
   * Create debug panel UI
   */
  createDebugPanel() {
    if (this.panel) return;

    const panel = document.createElement('div');
    panel.id = 'analytics-debug-panel';
    panel.innerHTML = `
      <style>
        #analytics-debug-panel {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 400px;
          max-height: 600px;
          background: white;
          border: 2px solid #2196F3;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          z-index: 999999;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          transition: all 0.3s ease;
        }

        #analytics-debug-panel.minimized {
          height: 50px;
          overflow: hidden;
        }

        .debug-header {
          background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
          color: white;
          padding: 12px 15px;
          cursor: move;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 6px 6px 0 0;
        }

        .debug-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }

        .debug-controls {
          display: flex;
          gap: 10px;
        }

        .debug-control-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 5px 10px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
        }

        .debug-control-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .debug-body {
          max-height: 500px;
          overflow-y: auto;
          padding: 10px;
          background: #f8f9fa;
        }

        .debug-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 15px;
        }

        .debug-stat {
          background: white;
          padding: 10px;
          border-radius: 6px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .debug-stat-value {
          font-size: 20px;
          font-weight: bold;
          color: #2196F3;
        }

        .debug-stat-label {
          font-size: 11px;
          color: #666;
          margin-top: 3px;
        }

        .debug-events {
          background: white;
          border-radius: 6px;
          padding: 10px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .debug-event {
          padding: 8px;
          margin-bottom: 8px;
          background: #f8f9fa;
          border-left: 3px solid #4CAF50;
          border-radius: 4px;
          font-size: 12px;
          transition: all 0.2s;
          cursor: pointer;
        }

        .debug-event:hover {
          background: #e8f5e9;
          transform: translateX(2px);
        }

        .debug-event.gtm {
          border-left-color: #2196F3;
        }

        .debug-event.analytics {
          border-left-color: #9C27B0;
        }

        .debug-event.error {
          border-left-color: #f44336;
          background: #ffebee;
        }

        .debug-event-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5px;
        }

        .debug-event-type {
          font-weight: 600;
          color: #333;
        }

        .debug-event-time {
          color: #999;
          font-size: 11px;
        }

        .debug-event-details {
          color: #666;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s;
        }

        .debug-event.expanded .debug-event-details {
          max-height: 200px;
          margin-top: 8px;
        }

        .debug-event-json {
          background: #263238;
          color: #aed581;
          padding: 8px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 11px;
          overflow-x: auto;
        }

        .debug-filter {
          display: flex;
          gap: 5px;
          margin-bottom: 10px;
        }

        .debug-filter-btn {
          padding: 5px 10px;
          border: 1px solid #ddd;
          background: white;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .debug-filter-btn.active {
          background: #2196F3;
          color: white;
          border-color: #2196F3;
        }

        .debug-activation-info {
          background: #e3f2fd;
          padding: 8px;
          border-radius: 4px;
          margin-bottom: 10px;
          font-size: 11px;
          color: #1565c0;
        }
        .debug-providers-grid {\n          display: grid;\n          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));\n          gap: 8px;\n          margin-bottom: 10px;\n        }\n\n        .debug-provider {\n          background: white;\n          padding: 8px;\n          border-radius: 4px;\n          text-align: center;\n          font-size: 11px;\n          border: 1px solid #eee;\n        }\n\n        .debug-provider.enabled {\n          border-color: #4CAF50;\n          background: #f1f8e9;\n        }\n\n        .debug-provider.disabled {\n          border-color: #f44336;\n          background: #ffebee;\n        }\n\n        .debug-provider-name {\n          font-weight: 600;\n          margin-bottom: 3px;\n        }\n\n        .debug-provider-status {\n          font-size: 10px;\n          color: #666;\n        }\n\n        .debug-consent-status {\n          background: white;\n          padding: 10px;\n          border-radius: 4px;\n          font-size: 11px;\n        }\n\n        .debug-consent-item {\n          display: flex;\n          justify-content: space-between;\n          padding: 4px 0;\n          border-bottom: 1px solid #f0f0f0;\n        }\n\n        .debug-consent-item:last-child {\n          border-bottom: none;\n        }\n\n        .debug-consent-value {\n          font-weight: 600;\n        }\n\n        .debug-consent-value.granted {\n          color: #4CAF50;\n        }\n\n        .debug-consent-value.denied {\n          color: #f44336;\n        }\n      </style>

      <div class="debug-header">
        <h3>📊 Analytics Debugger</h3>
        <div class="debug-controls">
          <button class="debug-control-btn" onclick="analyticsDebugger.clear()">Clear</button>
          <button class="debug-control-btn" onclick="analyticsDebugger.export()">Export</button>
          <button class="debug-control-btn" onclick="analyticsDebugger.minimize()">_</button>
          <button class="debug-control-btn" onclick="analyticsDebugger.disable()">✕</button>
        </div>
      </div>

      <div class="debug-body">
        <div class="debug-activation-info">
          ${this.backendConfig.enabled ?
            '🟢 Force-enabled by backend configuration' :
            this.enabled ?
              `🔐 Active via: ${Object.entries(this.activationMethods)
                .filter(([_, active]) => active)
                .map(([method]) => method)
                .join(', ')}` :
              '🟡 Ready for manual activation'
          }
        </div>

        <div class="debug-stats">
          <div class="debug-stat">
            <div class="debug-stat-value" id="debug-total-events">0</div>
            <div class="debug-stat-label">Total Events</div>
          </div>
          <div class="debug-stat">
            <div class="debug-stat-value" id="debug-gtm-events">0</div>
            <div class="debug-stat-label">GTM Events</div>
          </div>
          <div class="debug-stat">
            <div class="debug-stat-value" id="debug-provider-events">0</div>
            <div class="debug-stat-label">Provider Events</div>
          </div>
        </div>

        <div class="debug-filter">
          <button class="debug-filter-btn active" data-filter="all">All</button>
          <button class="debug-filter-btn" data-filter="gtm">GTM</button>
          <button class="debug-filter-btn" data-filter="analytics">Analytics</button>
          <button class="debug-filter-btn" data-filter="error">Errors</button>
        </div>

        <div class="debug-events" id="debug-events-list">
          <!-- Events will be added here -->
        </div>
      </div>
    `;

    document.body.appendChild(panel);
    this.panel = panel;

    // Make panel draggable
    this.makeDraggable(panel);

    // Setup filter buttons
    this.setupFilters();

    // Add enhanced sections after a short delay
    setTimeout(() => {
      this.addProviderSection();
      this.addConsentSection();
      this.addTestButtons();
      this.updateStatus();

      // Update status every 5 seconds
      setInterval(() => this.updateStatus(), 5000);
    }, 500);
  }

  /**
   * Make panel draggable
   */
  makeDraggable(element) {
    const header = element.querySelector('.debug-header');
    let isDragging = false;
    let currentX;
    let currentY;
    let initialX;
    let initialY;
    let xOffset = 0;
    let yOffset = 0;

    header.addEventListener('mousedown', (e) => {
      initialX = e.clientX - xOffset;
      initialY = e.clientY - yOffset;

      if (e.target === header || e.target.tagName === 'H3') {
        isDragging = true;
      }
    });

    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;
        xOffset = currentX;
        yOffset = currentY;

        element.style.transform = `translate(${currentX}px, ${currentY}px)`;
      }
    });

    document.addEventListener('mouseup', () => {
      isDragging = false;
    });
  }

  /**
   * Setup filter buttons
   */
  setupFilters() {
    const filterButtons = this.panel.querySelectorAll('.debug-filter-btn');

    filterButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        filterButtons.forEach(b => b.classList.remove('active'));
        e.target.classList.add('active');

        const filter = e.target.dataset.filter;
        this.filterEvents(filter);
      });
    });
  }

  /**
   * Filter events display
   */
  filterEvents(filter) {
    const events = this.panel.querySelectorAll('.debug-event');

    events.forEach(event => {
      if (filter === 'all' || event.classList.contains(filter)) {
        event.style.display = 'block';
      } else {
        event.style.display = 'none';
      }
    });
  }

  /**
   * Intercept analytics calls
   */
  interceptAnalytics() {
    // Intercept GTM pushes
    if (window.dataLayer) {
      const originalPush = window.dataLayer.push;
      window.dataLayer.push = (...args) => {
        this.captureEvent('GTM', args[0]);
        return originalPush.apply(window.dataLayer, args);
      };
    }

    // Intercept Analytics Manager
    if (window.$analytics) {
      const originalTrack = window.$analytics.track;
      window.$analytics.track = (eventName, eventData) => {
        this.captureEvent('Analytics Manager', { event: eventName, ...eventData });
        return originalTrack.call(window.$analytics, eventName, eventData);
      };
    }

    // Intercept direct gtag calls
    if (window.gtag) {
      const originalGtag = window.gtag;
      window.gtag = (...args) => {
        this.captureEvent('gtag', { command: args[0], parameters: args.slice(1) });
        return originalGtag.apply(window, args);
      };
    }

    // Intercept Facebook Pixel
    if (window.fbq) {
      const originalFbq = window.fbq;
      window.fbq = (...args) => {
        this.captureEvent('Facebook Pixel', { command: args[0], parameters: args.slice(1) });
        return originalFbq.apply(window, args);
      };
    }
  }

  /**
   * Capture analytics event
   */
  captureEvent(source, data) {
    if (!this.enabled) return;

    const event = {
      id: Date.now() + Math.random(),
      source,
      data,
      timestamp: new Date().toISOString(),
      elapsed: Date.now() - this.startTime
    };

    this.events.unshift(event);

    // Keep only last N events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    this.updatePanel(event);
    this.logEvent(event);
  }

  /**
   * Update debug panel with new event
   */
  updatePanel(event) {
    if (!this.panel) return;

    // Update stats
    document.getElementById('debug-total-events').textContent = this.events.length;
    document.getElementById('debug-gtm-events').textContent =
      this.events.filter(e => e.source === 'GTM').length;
    document.getElementById('debug-provider-events').textContent =
      this.events.filter(e => e.source === 'Analytics Manager').length;

    // Add event to list
    const eventsList = document.getElementById('debug-events-list');
    const eventEl = document.createElement('div');
    eventEl.className = `debug-event ${event.source.toLowerCase().replace(' ', '-')}`;

    const eventName = event.data.event || event.data.command || 'Unknown';
    const timeStr = new Date(event.timestamp).toLocaleTimeString();

    eventEl.innerHTML = `
      <div class="debug-event-header">
        <span class="debug-event-type">${eventName}</span>
        <span class="debug-event-time">${timeStr}</span>
      </div>
      <div class="debug-event-details">
        <div class="debug-event-json">${JSON.stringify(event.data, null, 2)}</div>
      </div>
    `;

    eventEl.addEventListener('click', () => {
      eventEl.classList.toggle('expanded');
    });

    // Add to top of list
    if (eventsList.firstChild) {
      eventsList.insertBefore(eventEl, eventsList.firstChild);
    } else {
      eventsList.appendChild(eventEl);
    }

    // Remove old events from DOM
    while (eventsList.children.length > this.maxEvents) {
      eventsList.removeChild(eventsList.lastChild);
    }
  }

  /**
   * Log event to console
   */
  logEvent(event) {
    const style = this.getConsoleStyle(event.source);
    console.log(
      `%c${event.source}%c ${event.data.event || event.data.command || 'Event'}`,
      style,
      'color: #333; font-weight: normal;',
      event.data
    );
  }

  /**
   * Get console style for source
   */
  getConsoleStyle(source) {
    const styles = {
      'GTM': 'background: #2196F3; color: white; padding: 2px 6px; border-radius: 3px;',
      'Analytics Manager': 'background: #9C27B0; color: white; padding: 2px 6px; border-radius: 3px;',
      'gtag': 'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 3px;',
      'Facebook Pixel': 'background: #1877f2; color: white; padding: 2px 6px; border-radius: 3px;'
    };
    return styles[source] || 'background: #666; color: white; padding: 2px 6px; border-radius: 3px;';
  }

  /**
   * Clear all events
   */
  clear() {
    this.events = [];
    if (this.panel) {
      document.getElementById('debug-events-list').innerHTML = '';
      document.getElementById('debug-total-events').textContent = '0';
      document.getElementById('debug-gtm-events').textContent = '0';
      document.getElementById('debug-provider-events').textContent = '0';
    }
    console.log('%c📊 Analytics Debugger Cleared', 'background: #FF9800; color: white; padding: 5px 10px; border-radius: 3px;');
  }

  /**
   * Export events to JSON
   */
  export() {
    const exportData = {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      events: this.events,
      activationMethods: this.activationMethods
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-debug-${Date.now()}.json`;
    a.click();

    this.showNotification('Events exported to JSON', 'success');
  }

  /**
   * Minimize panel
   */
  minimize() {
    if (this.panel) {
      this.panel.classList.toggle('minimized');
    }
  }

  /**
   * Show notification
   */
  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#2196F3'};
      color: white;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 999999;
      animation: slideIn 0.3s ease;
      font-family: 'Segoe UI', sans-serif;
      font-size: 14px;
    `;
    notification.textContent = message;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Get support activation key for today
   */
  static getSupportKey() {
    const today = new Date().toISOString().split('T')[0];
    return btoa('analytics_debug_' + today).substring(0, 10);
  }

  /**
   * Test method to force show debugger (for development)
   */
  testShow() {
    // Force enable with test config
    this.backendConfig = {
      enabled: true,
      allowed: true,
      keyboard_activation: true,
      time_limit_minutes: 60
    };

    this.activationMethods.backend = true;
    this.enable('Test/Development Mode');

    console.log('%c📊 Analytics Debugger Test Mode', 'background: #FF9800; color: white; padding: 5px 10px; border-radius: 3px;', 'Debugger enabled for testing');

    // Add some test events to make it interesting
    setTimeout(() => {
      this.captureEvent('Test', { event: 'test_page_view', test: true });
      this.captureEvent('GTM', { event: 'test_gtm_event', test: true });
      this.captureEvent('Analytics Manager', { event: 'test_analytics_event', test: true });
    }, 1000);
  }

  /**
   * Add provider status section
   */
  addProviderSection() {
    if (!this.panel) return;
    const statsSection = this.panel.querySelector('.debug-stats');
    if (!statsSection || this.panel.querySelector('#debug-providers-section')) return;

    const providerSection = document.createElement('div');
    providerSection.id = 'debug-providers-section';
    providerSection.innerHTML = `
      <h4 style="margin: 15px 0 10px 0; font-size: 13px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">📡 Analytics Providers</h4>
      <div id="debug-providers-grid">
        <div style="color: #666; font-size: 11px; padding: 10px; background: white; border-radius: 4px;">Loading providers...</div>
      </div>
    `;
    statsSection.insertAdjacentElement('afterend', providerSection);
  }

  /**
   * Add consent status section
   */
  addConsentSection() {
    if (!this.panel) return;
    const providerSection = this.panel.querySelector('#debug-providers-section');
    if (!providerSection || this.panel.querySelector('#debug-consent-section')) return;

    const consentSection = document.createElement('div');
    consentSection.id = 'debug-consent-section';
    consentSection.innerHTML = `
      <h4 style="margin: 15px 0 10px 0; font-size: 13px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">🍪 Cookie Consent</h4>
      <div id="debug-consent-status">
        <div style="color: #666; font-size: 11px; padding: 10px; background: white; border-radius: 4px;">Loading consent...</div>
      </div>
    `;
    providerSection.insertAdjacentElement('afterend', consentSection);
  }

  /**
   * Update all status sections
   */
  updateStatus() {
    this.updateProviderStatus();
    this.updateConsentStatus();
    this.updateBackendInfo();
  }

  /**
   * Update provider status display
   */
  updateProviderStatus() {
    const grid = this.panel?.querySelector('#debug-providers-grid');
    if (!grid) return;

    const providers = [];
    const analyticsManager = window.$analytics;

    // Check Analytics Manager
    if (analyticsManager && typeof analyticsManager.getStatus === 'function') {
      try {
        const status = analyticsManager.getStatus();
        providers.push({
          name: 'Analytics Manager',
          enabled: status.initialized,
          status: status.initialized ? `Ready (${Object.keys(status.providers || {}).length} providers)` : 'Not Initialized'
        });

        Object.entries(status.providers || {}).forEach(([name, provider]) => {
          providers.push({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            enabled: provider.enabled && provider.initialized,
            status: provider.enabled ? (provider.initialized ? 'Ready' : 'Loading') : 'Disabled'
          });
        });
      } catch (error) {
        providers.push({
          name: 'Analytics Manager',
          enabled: false,
          status: 'Error: ' + error.message
        });
      }
    } else {
      providers.push({
        name: 'Analytics Manager',
        enabled: !!analyticsManager,
        status: analyticsManager ? 'Available (no getStatus)' : 'Not Found'
      });
    }

    // Check GTM
    const hasGtag = typeof window.gtag !== 'undefined';
    const hasDataLayer = typeof window.dataLayer !== 'undefined';
    providers.push({
      name: 'GTM',
      enabled: hasGtag || hasDataLayer,
      status: hasGtag ? 'Ready (gtag)' : hasDataLayer ? 'DataLayer Only' : 'Not Loaded'
    });

    // Check GA4
    providers.push({
      name: 'GA4',
      enabled: hasGtag,
      status: hasGtag ? 'Ready' : 'Not Loaded'
    });

    grid.innerHTML = providers.map(provider => `
      <div style="margin: 5px 0; padding: 8px; border: 1px solid ${provider.enabled ? '#4CAF50' : '#f44336'};
                  border-radius: 4px; background: ${provider.enabled ? '#f1f8e9' : '#ffebee'};">
        <div style="font-weight: 600; font-size: 11px; margin-bottom: 2px;">${provider.name}</div>
        <div style="font-size: 10px; color: #666;">${provider.status}</div>
      </div>
    `).join('');
  }

  /**
   * Update consent status display
   */
  updateConsentStatus() {
    const statusDiv = this.panel?.querySelector('#debug-consent-status');
    if (!statusDiv) return;

    const consent = window.analyticsConsent || {};
    const hasConsent = Object.keys(consent).length > 0;

    if (!hasConsent) {
      statusDiv.innerHTML = `
        <div style="color: #ff9800; font-size: 11px; padding: 10px; background: #fff3e0; border-radius: 4px; text-align: center;">
          ⚠️ No consent data found<br>
          <small>User may not have interacted with cookie banner</small>
        </div>
      `;
      return;
    }

    const consentItems = [
      { label: 'Technical', value: consent.technical !== false },
      { label: 'Analytics', value: consent.analytics === true },
      { label: 'Marketing', value: consent.marketing === true },
      { label: 'Experience', value: consent.experience === true }
    ];

    statusDiv.innerHTML = `
      <div style="background: white; border-radius: 4px; padding: 8px;">
        ${consentItems.map(item => `
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 0;
                      border-bottom: 1px solid #f0f0f0; font-size: 11px;">
            <span style="font-weight: 500;">${item.label}</span>
            <span style="font-weight: 600; color: ${item.value ? '#4CAF50' : '#f44336'};">
              ${item.value ? '✅ Granted' : '❌ Denied'}
            </span>
          </div>
        `).join('')}
        ${consent.timestamp ? `
          <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee; font-size: 10px; color: #666; text-align: center;">
            Last updated: ${new Date(consent.timestamp).toLocaleString()}
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Update backend configuration info
   */
  updateBackendInfo() {
    const activationInfo = this.panel?.querySelector('.debug-activation-info');
    if (!activationInfo) return;

    // Show network data analytics IDs if available
    let backendStatus = 'No config received from backend';
    if (this.networkData && Object.keys(this.networkData).length > 0) {
      const analyticsIds = [];
      if (this.networkData.google_tag_manager_id) analyticsIds.push('GTM');
      if (this.networkData.google_analytics_id) analyticsIds.push('GA4');
      if (this.networkData.google_ads_conversion_id) analyticsIds.push('Google Ads');
      if (this.networkData.facebook_pixel_id) analyticsIds.push('Facebook');
      if (this.networkData.hotjar_site_id) analyticsIds.push('Hotjar');
      if (this.networkData.microsoft_clarity_id) analyticsIds.push('Clarity');
      if (this.networkData.google_optimize_id) analyticsIds.push('Optimize');

      if (analyticsIds.length > 0) {
        backendStatus = `${analyticsIds.length} services configured: ${analyticsIds.join(', ')}`;
      } else {
        backendStatus = 'Network data loaded, no analytics IDs found';
      }
    }

    let analyticsConfig = 'No analytics configuration loaded';

    // Get config through proper API
    if (window.$analytics && typeof window.$analytics.getStatus === 'function') {
      try {
        const status = window.$analytics.getStatus();
        if (status.config) {
          const enabledProviders = Object.entries(status.config)
            .filter(([_, config]) => config.enabled)
            .map(([name]) => name);
          analyticsConfig = `${enabledProviders.length} providers enabled: ${enabledProviders.join(', ')}`;
        }
      } catch (error) {
        analyticsConfig = 'Error reading analytics config';
      }
    }

    const currentContent = activationInfo.innerHTML.split('<br>')[0]; // Keep the activation status

    activationInfo.innerHTML = `
      ${currentContent}
      <br><small style="color: #666; margin-top: 4px; display: block;">Network Data: ${backendStatus}</small>
      <br><small style="color: #666;">Analytics Manager: ${analyticsConfig}</small>
    `;
  }

  // ============================================
  // TEST TRIGGERS
  // ============================================

  /**
   * Test suite for triggering analytics events
   */
  runTestSuite() {
    if (!this.enabled) {
      console.warn('[Analytics Debugger] Enable debugger first');
      return;
    }

    console.log('%c🧪 Running Analytics Test Suite', 'background: #9C27B0; color: white; padding: 5px 10px; border-radius: 3px;');

    // Test e-commerce events
    setTimeout(() => this.testPageView(), 100);
    setTimeout(() => this.testViewItem(), 600);
    setTimeout(() => this.testAddToCart(), 1200);
    setTimeout(() => this.testRemoveFromCart(), 1800);
    setTimeout(() => this.testBeginCheckout(), 2400);
    setTimeout(() => this.testPurchase(), 3000);
    setTimeout(() => this.testSearch(), 3600);
    setTimeout(() => this.testUserEngagement(), 4200);

    setTimeout(() => {
      console.log('%c✅ Test Suite Complete', 'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px;');
    }, 4800);
  }

  /**
   * Test page view event
   */
  testPageView() {
    const event = {
      page_title: 'Test Page - Analytics Debug',
      page_location: window.location.href,
      page_path: '/test-debug',
      user_id: 'debug_user_' + Date.now()
    };

    if (window.$analytics) {
      window.$analytics.track('page_view', event);
      console.log('📄 Page view test sent');
    }
  }

  /**
   * Test view item event
   */
  testViewItem() {
    const event = {
      currency: 'EUR',
      value: 199.00,
      items: [{
        item_id: 'DEBUG_ROOM_001',
        item_name: 'Debug Suite Room',
        item_category: 'Accommodation',
        item_category2: 'Suite',
        item_brand: 'Debug Hotel',
        price: 199.00,
        quantity: 1,
        guests: 2,
        nights: 2
      }]
    };

    if (window.$analytics) {
      window.$analytics.track('view_item', event);
      console.log('👁️ View item test sent');
    }
  }

  /**
   * Test add to cart event
   */
  testAddToCart() {
    const event = {
      currency: 'EUR',
      value: 199.00,
      items: [{
        item_id: 'DEBUG_ROOM_001',
        item_name: 'Debug Suite Room',
        price: 199.00,
        quantity: 1,
        item_category: 'Accommodation'
      }]
    };

    if (window.$analytics) {
      window.$analytics.track('add_to_cart', event);
      console.log('🛒 Add to cart test sent');
    }
  }

  /**
   * Test remove from cart event
   */
  testRemoveFromCart() {
    const event = {
      currency: 'EUR',
      value: 199.00,
      items: [{
        item_id: 'DEBUG_ROOM_001',
        item_name: 'Debug Suite Room',
        price: 199.00,
        quantity: 1
      }]
    };

    if (window.$analytics) {
      window.$analytics.track('remove_from_cart', event);
      console.log('🗑️ Remove from cart test sent');
    }
  }

  /**
   * Test begin checkout event
   */
  testBeginCheckout() {
    const event = {
      currency: 'EUR',
      value: 398.00,
      coupon: 'DEBUG20',
      items: [{
        item_id: 'DEBUG_ROOM_001',
        item_name: 'Debug Suite Room',
        price: 199.00,
        quantity: 2
      }]
    };

    if (window.$analytics) {
      window.$analytics.track('begin_checkout', event);
      console.log('💳 Begin checkout test sent');
    }
  }

  /**
   * Test purchase event
   */
  testPurchase() {
    const transactionId = 'DEBUG_TXN_' + Date.now();
    const event = {
      transaction_id: transactionId,
      value: 378.00,
      tax: 38.00,
      shipping: 0,
      currency: 'EUR',
      coupon: 'DEBUG20',
      items: [{
        item_id: 'DEBUG_ROOM_001',
        item_name: 'Debug Suite Room',
        price: 170.00, // After discount
        quantity: 2,
        discount: 58.00
      }]
    };

    if (window.$analytics) {
      window.$analytics.track('purchase', event);
      console.log(`🎉 Purchase test sent (${transactionId})`);
    }
  }

  /**
   * Test search event
   */
  testSearch() {
    const event = {
      search_term: 'Debug Hotel Florence',
      location: 'Florence, Italy',
      check_in: '2024-12-15',
      check_out: '2024-12-17',
      adults: 2,
      children: 0,
      rooms: 1,
      nights: 2
    };

    if (window.$analytics) {
      window.$analytics.track('search', event);
      console.log('🔍 Search test sent');
    }
  }

  /**
   * Test user engagement event
   */
  testUserEngagement() {
    const event = {
      engagement_time_msec: 45000,
      percent_scrolled: 75
    };

    if (window.$analytics) {
      window.$analytics.track('user_engagement', event);
      console.log('👤 User engagement test sent');
    }
  }

  /**
   * Test consent scenarios
   */
  testConsentScenarios() {
    if (!this.enabled) {
      console.warn('[Analytics Debugger] Enable debugger first');
      return;
    }

    console.log('%c🍪 Testing Consent Scenarios', 'background: #FF9800; color: white; padding: 5px 10px; border-radius: 3px;');

    // Test 1: All cookies accepted
    setTimeout(() => {
      const consent = {
        technical: true,
        analytics: true,
        marketing: true,
        experience: true
      };
      if (window.$analytics?.updateConsent) {
        window.$analytics.updateConsent(consent);
        console.log('✅ All cookies accepted test');
      }
    }, 500);

    // Test 2: Only essential cookies
    setTimeout(() => {
      const consent = {
        technical: true,
        analytics: false,
        marketing: false,
        experience: false
      };
      if (window.$analytics?.updateConsent) {
        window.$analytics.updateConsent(consent);
        console.log('🔒 Essential only test');
      }
    }, 2000);

    // Test 3: Partial consent
    setTimeout(() => {
      const consent = {
        technical: true,
        analytics: true,
        marketing: false,
        experience: true
      };
      if (window.$analytics?.updateConsent) {
        window.$analytics.updateConsent(consent);
        console.log('⚖️ Partial consent test');
      }
    }, 3500);
  }

  /**
   * Test all providers individually
   */
  testProviders() {
    if (!this.enabled) {
      console.warn('[Analytics Debugger] Enable debugger first');
      return;
    }

    if (!window.$analytics?.providers) {
      console.error('[Analytics Debugger] Analytics Manager not available');
      return;
    }

    console.log('%c📡 Testing Individual Providers', 'background: #3F51B5; color: white; padding: 5px 10px; border-radius: 3px;');

    const providers = Array.from(window.$analytics.providers.keys());
    console.log('Available providers:', providers);

    providers.forEach((providerName, index) => {
      setTimeout(() => {
        const testEvent = {
          test_provider: providerName,
          timestamp: Date.now(),
          test_id: `provider_test_${index}`
        };

        window.$analytics.track('debug_provider_test', testEvent);
        console.log(`🧪 Testing ${providerName} provider`);
      }, index * 500);
    });
  }

  /**
   * Stress test - send multiple events rapidly
   */
  stressTest() {
    if (!this.enabled) {
      console.warn('[Analytics Debugger] Enable debugger first');
      return;
    }

    console.log('%c⚡ Running Stress Test', 'background: #E91E63; color: white; padding: 5px 10px; border-radius: 3px;');
    console.log('Sending 20 events in 5 seconds...');

    for (let i = 0; i < 20; i++) {
      setTimeout(() => {
        if (window.$analytics) {
          window.$analytics.track('stress_test_event', {
            event_number: i + 1,
            timestamp: Date.now(),
            random_data: Math.random().toString(36).substring(7)
          });
        }
      }, i * 250); // 250ms intervals
    }

    setTimeout(() => {
      console.log('%c✅ Stress Test Complete', 'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px;');
    }, 5500);
  }

  /**
   * Add test triggers to debug panel
   */
  addTestButtons() {
    if (!this.panel) return;

    const testSection = document.createElement('div');
    testSection.innerHTML = `
      <h4 style="margin: 15px 0 10px 0; font-size: 13px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">🧪 Test Triggers</h4>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px;">
        <button onclick="analyticsDebugger.runTestSuite()" style="padding: 6px 8px; font-size: 11px; border: 1px solid #ddd; background: #f8f9fa; border-radius: 4px; cursor: pointer;">Full Test Suite</button>
        <button onclick="analyticsDebugger.testConsentScenarios()" style="padding: 6px 8px; font-size: 11px; border: 1px solid #ddd; background: #f8f9fa; border-radius: 4px; cursor: pointer;">Consent Tests</button>
        <button onclick="analyticsDebugger.testProviders()" style="padding: 6px 8px; font-size: 11px; border: 1px solid #ddd; background: #f8f9fa; border-radius: 4px; cursor: pointer;">Provider Tests</button>
        <button onclick="analyticsDebugger.stressTest()" style="padding: 6px 8px; font-size: 11px; border: 1px solid #ddd; background: #f8f9fa; border-radius: 4px; cursor: pointer;">Stress Test</button>
      </div>
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 10px;">
        <button onclick="analyticsDebugger.testPageView()" style="padding: 4px 6px; font-size: 10px; border: 1px solid #ddd; background: #fff; border-radius: 3px; cursor: pointer;">Page View</button>
        <button onclick="analyticsDebugger.testViewItem()" style="padding: 4px 6px; font-size: 10px; border: 1px solid #ddd; background: #fff; border-radius: 3px; cursor: pointer;">View Item</button>
        <button onclick="analyticsDebugger.testAddToCart()" style="padding: 4px 6px; font-size: 10px; border: 1px solid #ddd; background: #fff; border-radius: 3px; cursor: pointer;">Add Cart</button>
        <button onclick="analyticsDebugger.testPurchase()" style="padding: 4px 6px; font-size: 10px; border: 1px solid #ddd; background: #fff; border-radius: 3px; cursor: pointer;">Purchase</button>
      </div>
    `;

    // Insert after stats section
    const statsSection = this.panel.querySelector('.debug-stats');
    if (statsSection) {
      statsSection.insertAdjacentElement('afterend', testSection);
    }
  }
}

// Create global instance
window.analyticsDebugger = new AnalyticsDebugger();

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);

export default AnalyticsDebugger;